import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { useNavigate } from 'react-router-dom';
import { useLayoutContext } from '../../components/layout/Layout';
import { getMakecodeArcadePageRootStyle } from './makecodeArcadePageStyles';
import Button from '../../components/ui/Button';
import ArcadeGamePoster from './ArcadeGamePoster';

interface Game {
  id: string;
  title: string;
  description: string;
  gameUrl: string;
  simulatorCode: string;
  processUrl: string;
  companyName: string;
  creatorName: string;
  schoolName: string;
  schoolId: string;
  userId: string;
  createdAt: Date;
  rating: number;
  playCount: number;
  iconUrl?: string;
}

const ArcadeMain: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useLayoutContext();
  const [games, setGames] = useState<Game[]>([]);
  const [popularGames, setPopularGames] = useState<Game[]>([]);
  const [topRatedGames, setTopRatedGames] = useState<Game[]>([]);
  const [featuredGame, setFeaturedGame] = useState<Game | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchGames = async () => {
      try {
        setLoading(true);
        setError(null);

        const gamesRef = collection(db, 'games');
        const gamesSnapshot = await getDocs(gamesRef);
        const gamesList = gamesSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
          };
        }) as Game[];

        setGames(gamesList);

        const popularQuery = query(gamesRef, orderBy('playCount', 'desc'), limit(8));
        const popularSnapshot = await getDocs(popularQuery);
        setPopularGames(popularSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || new Date(),
        })) as Game[]);

        const ratedQuery = query(gamesRef, orderBy('rating', 'desc'), limit(8));
        const ratedSnapshot = await getDocs(ratedQuery);
        setTopRatedGames(ratedSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || new Date(),
        })) as Game[]);

        if (gamesList.length > 0) {
          const scoredGames = gamesList.map(game => ({
            ...game,
            score: (game.playCount * 0.7) + (game.rating * 0.3),
          }));
          scoredGames.sort((a, b) => b.score - a.score);
          setFeaturedGame(scoredGames[0]);
        }
      } catch (fetchError) {
        console.error('Error fetching games:', fetchError);
        setError(fetchError instanceof Error ? fetchError.message : '게임을 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchGames();
  }, []);

  const handleGameClick = (gameId: string) => {
    navigate(`/makecodearcade/game/${gameId}`);
  };

  const newestGames = [...games]
    .sort((a, b) => (b.createdAt?.getTime?.() || 0) - (a.createdAt?.getTime?.() || 0))
    .slice(0, 8);

  const renderTile = (game: Game) => {
    return (
      <button
        key={game.id}
        type="button"
        className="arcade-tile"
        onClick={() => handleGameClick(game.id)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          padding: 0,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          textAlign: 'left',
          minWidth: 0,
          color: 'inherit',
        }}
      >
        <div style={{
          aspectRatio: '1 / 1',
          borderRadius: '16px',
          overflow: 'hidden',
          backgroundColor: 'var(--Color-background-normal, white)',
        }}>
          <ArcadeGamePoster id={game.id} title={game.title} iconUrl={game.iconUrl} />
        </div>
        <div style={{ minWidth: 0, padding: '0 2px' }}>
          <div style={{
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '20px',
            color: '#F4F4F8',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {game.title}
          </div>
          <div style={{
            marginTop: '2px',
            fontSize: '12px',
            lineHeight: '18px',
            color: 'var(--Color-text-assistive, #989898)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {game.schoolName || game.creatorName || 'MARU'} · {(game.playCount || 0).toLocaleString()}회
          </div>
        </div>
      </button>
    );
  };

  const renderSection = (title: string, items: Game[]) => {
    if (items.length === 0) return null;
    return (
      <section style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <h2 style={{
          margin: 0,
          fontSize: isMobile ? '18px' : '20px',
          fontWeight: 800,
          lineHeight: '28px',
          color: '#F7F7FB',
          letterSpacing: '-0.02em',
        }}>
          {title}
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))',
          gap: isMobile ? '14px 10px' : '18px 14px',
        }}>
          {items.map(renderTile)}
        </div>
      </section>
    );
  };

  if (error) {
    return (
      <div style={{ ...getMakecodeArcadePageRootStyle(isMobile), textAlign: 'center' }}>
        <h2 style={{ color: '#FF8A80', marginBottom: '16px', fontFamily: 'Pretendard' }}>
          {error}
        </h2>
        <Button variant="primary" onClick={() => window.location.reload()}>
          다시 시도
        </Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '240px',
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #2A2A38',
          borderTop: '4px solid #FF6F0F',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }} />
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div
      style={getMakecodeArcadePageRootStyle(isMobile, {
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? '22px' : '28px',
      })}
    >
      <style>{`
        .arcade-tile {
          transition: transform 0.16s ease;
        }
        .arcade-tile:hover {
          transform: translateY(-4px);
        }
        .arcade-tile:focus-visible {
          outline: 2px solid #FF6F0F;
          outline-offset: 4px;
          border-radius: 16px;
        }
        .arcade-hero {
          transition: transform 0.16s ease;
        }
        .arcade-hero:hover {
          transform: translateY(-3px);
        }
        .arcade-hero:focus-visible {
          outline: 2px solid #FF6F0F;
          outline-offset: 4px;
        }
      `}</style>

      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: '12px',
      }}>
        <div>
          <h1 style={{
            margin: 0,
            fontSize: isMobile ? '26px' : '32px',
            fontWeight: 800,
            lineHeight: isMobile ? '36px' : '44px',
            letterSpacing: '-0.03em',
            color: '#F4F4F8',
          }}>
            오늘 뭐 할까
          </h1>
          <p style={{
            margin: '6px 0 0',
            fontSize: '14px',
            lineHeight: '22px',
            color: 'var(--Color-text-assistive, #989898)',
          }}>
            친구들이 만든 게임을 골라 바로 플레이해 봐.
          </p>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          <button
            type="button"
            onClick={() => navigate('/makecodearcade/register')}
            style={{
              border: 'none',
              borderRadius: '999px',
              padding: '10px 16px',
              background: '#FF6F0F',
              color: '#FFFFFF',
              fontWeight: 700,
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            내 게임 올리기
          </button>
          <button
            type="button"
            onClick={() => window.open('https://arcade.makecode.com', '_blank', 'noopener,noreferrer')}
            style={{
              border: '1px solid #3A3A4C',
              borderRadius: '999px',
              padding: '10px 16px',
              background: 'transparent',
              color: '#F4F4F8',
              fontWeight: 700,
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            게임 만들기
          </button>
          <button
            type="button"
            onClick={() => navigate('/makecodearcade/register-guide')}
            style={{
              border: '1px solid #3A3A4C',
              borderRadius: '999px',
              padding: '10px 16px',
              background: 'transparent',
              color: '#F4F4F8',
              fontWeight: 700,
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            등록 가이드
          </button>
        </div>
      </div>

      {featuredGame && (
        <button
          type="button"
          className="arcade-hero"
          onClick={() => handleGameClick(featuredGame.id)}
          style={{
            position: 'relative',
            display: 'block',
            width: isMobile ? '100%' : '360px',
            aspectRatio: '1 / 1',
            height: 'auto',
            padding: 0,
            border: 'none',
            borderRadius: '20px',
            overflow: 'hidden',
            cursor: 'pointer',
            textAlign: 'left',
            color: 'white',
          }}
        >
          <span style={{ position: 'absolute', inset: 0 }}>
            <ArcadeGamePoster id={featuredGame.id} title={featuredGame.title} iconUrl={featuredGame.iconUrl} fontSize={72} />
          </span>
          <span style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(180deg, rgba(0,0,0,0.05) 20%, rgba(0,0,0,0.72) 100%)',
          }} />
          <span style={{
            position: 'absolute',
            left: isMobile ? '16px' : '24px',
            right: isMobile ? '16px' : '24px',
            bottom: isMobile ? '16px' : '22px',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
          }}>
            <span style={{
              alignSelf: 'flex-start',
              padding: '4px 10px',
              borderRadius: '999px',
              background: '#FF6F0F',
              fontSize: '12px',
              fontWeight: 700,
              lineHeight: '18px',
            }}>
              지금 핫한 게임
            </span>
            <span style={{
              fontSize: isMobile ? '24px' : '34px',
              fontWeight: 800,
              lineHeight: isMobile ? '32px' : '44px',
              letterSpacing: '-0.03em',
              textShadow: '0 2px 12px rgba(0,0,0,0.45)',
            }}>
              {featuredGame.title}
            </span>
            <span style={{ fontSize: '14px', color: 'rgba(255,255,255,0.86)' }}>
              {featuredGame.creatorName || '익명'}
              {featuredGame.schoolName ? ` · ${featuredGame.schoolName}` : ''}
              {` · ${(featuredGame.playCount || 0).toLocaleString()}회`}
            </span>
          </span>
        </button>
      )}

      {games.length === 0 && (
        <div style={{
          padding: '28px 16px',
          borderRadius: '16px',
          background: '#1C1C28',
          color: '#C8C8D4',
          textAlign: 'center',
        }}>
          아직 올라온 게임이 없어요. 첫 게임을 올려 보세요.
        </div>
      )}

      {renderSection('오늘 인기', popularGames.filter(game => game.id !== featuredGame?.id))}
      {renderSection('새로 올라온 게임', newestGames.filter(game => game.id !== featuredGame?.id))}
      {renderSection('점수 높은 게임', topRatedGames.filter(game => game.id !== featuredGame?.id))}
    </div>
  );
};

export default ArcadeMain;
